import React from "react";
import { Position } from "reactflow";

const parseCHNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  return parseFloat(String(v).replace(/'/g, "")) || 0;
};

const formatNumberCH = (value, options = {}) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    return value?.toString ? value.toString() : "";
  }
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = Math.max(minimumFractionDigits, 2),
  } = options;

  return numberValue.toLocaleString("de-CH", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

const SUBTITLE_CHAR_LIMIT = 30;

const truncateText = (text, limit = SUBTITLE_CHAR_LIMIT) => {
  if (!text || text.length <= limit) return text;
  return text.slice(0, limit).trimEnd() + "…";
};

const formatNumberWithUnit = (value, unit) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  const formatted = Number.isFinite(numericValue)
    ? formatNumberCH(numericValue, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : value;
  return unit ? `${formatted} ${unit}` : formatted;
};

// Column pitch is 280px so the restored 210px-wide boxes keep a ~70px horizontal
// gap between columns (the old 250px pitch left only 40px once boxes grew to 210).
export const columnX = {
  incoming: 30,
  root: 310,           // Column 1: Root key
  destination: 590,    // Column 2: First set of destinations (blue boxes)
  buyer1: 870,         // Column 3: Buyers/targets of Column 2
  destination2: 1150,  // Column 4: Second set of destinations (blue boxes)
  buyer2: 1430,        // Column 5: Buyers/targets of Column 4
};

export const STANDARD_A4_FRAME = {
  widthPx: 794,
  minHeightPx: 1123,
};

// 14 destinations per page — pairs with MAX_DESTINATIONS_PER_COLUMN=7 for a
// BALANCED 7+7 layout. Portrait A4 has ample vertical room: 7-row layout is
// ~1738px tall × 1680px wide (aspect ~0.97:1, near-square) which scales to
// ~566pt on the ~654pt available band — fits with ~110pt to spare, no footer
// overlap. Keeps the layout aspect closer to portrait so the tree fills more
// of the page vertically vs the earlier 6+6 (aspect 1.19:1 left a big gap
// under the tree). Cost box still occupies its own top-left band above the
// tree, unaffected by row count.
export const DEFAULT_DESTINATIONS_PER_PAGE = 14;

// Box size restored to the larger, readable dimensions the client approved in the
// previous version (the submitted build shrank these to 160×65, which crowded the
// code/label/value lines and made scroll positions look misaligned). Kept in sync
// with NODE_W in the spacer calc below and the column pitch in columnX.
const NODE_WIDTH = 210;
const nodeBaseStyle = {
  border: "1px solid #d8dee9",
  borderRadius: 10,
  padding: 8,
  width: NODE_WIDTH,
  minHeight: 80,
  fontSize: 20,
  background: "#fff",
};

export const nodeStyles = {
  root: {
    ...nodeBaseStyle,
    background: "#A8A8A8",
    border: "2px solid #8A8A8A",
  },
  incoming: {
    ...nodeBaseStyle,
    background: "#A8A8A8",
    border: "2px solid #8A8A8A",
  },
  destination: {
    ...nodeBaseStyle,
    background: "#4A90E2",
    border: "2px solid #357ABD",
  },
  consumption: {
    ...nodeBaseStyle,
    background: "#4A90E2",
    border: "2px solid #357ABD",
  },
  surface: {
    ...nodeBaseStyle,
    background: "#003366",
    border: "2px solid #002244",
  },
  buyer: {
    ...nodeBaseStyle,
    background: "#708090",
    border: "2px solid #5A6A7A",
  },
  // Lighter grey so an object box is distinguishable from a normal buyer box.
  object: {
    ...nodeBaseStyle,
    background: "#C8CDD4",
    border: "2px solid #A9B0BA",
  },
  target: {
    ...nodeBaseStyle,
    background: "#B2F5EA",
    border: "2px solid #7FD4C1",
  },
};

export const NodeContent = ({
  title,
  subtitle,
  cost,
  costDetails = [],
  meta = [],
  textColor = "#ffffff",
  textAlign = "left",
  centerContent = false,
  showEditIcon = false,
  onEditClick,
}) => {
  const dividerStyle = {
    margin: "6px 0",
    width: "100%",
    height: 0,
    border: "none",
    borderTop: "2px solid #ffffff",
    display: "block",
  };

  return (
    <div
      className="diagram-node"
      style={{
        wordWrap: "break-word",
        overflowWrap: "break-word",
        fontSize: "20px",
        maxHeight: "180px",
        overflowY: "hidden",
        overflowX: "hidden",
        paddingRight: "4px",
        fontFamily: '"PP Neue Montreal", sans-serif',
        color: textColor,
        textAlign,
        display: centerContent ? "flex" : "block",
        flexDirection: centerContent ? "column" : undefined,
        justifyContent: centerContent ? "center" : undefined,
        alignItems: centerContent
          ? textAlign === "center"
            ? "center"
            : textAlign === "right"
            ? "flex-end"
            : "flex-start"
          : undefined,
        height: centerContent ? "100%" : "auto",
        minHeight: centerContent ? "100%" : "auto",
        width: "100%",
        position: "relative",
      }}
    >
      {showEditIcon && onEditClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditClick();
          }}
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            background: "#ffffff",
            border: "1px solid #d8dee9",
            borderRadius: "4px",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            zIndex: 10,
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f5f5f5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#ffffff";
          }}
          title="Edit Key"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#00033F"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      )}
      {cost && (
        <>
          <div
            style={{
              wordBreak: "break-word",
              lineHeight: "1.25",
              fontSize: "22px",
              fontWeight: 600,
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
            title={cost}
          >
            {cost}
          </div>
          <div style={dividerStyle} />
        </>
      )}
      <div
        className="fw-bold"
        style={{
          wordBreak: "break-word",
          lineHeight: "1.25",
          fontSize: "22px",
          color: textColor,
          fontFamily: '"PP Neue Montreal", sans-serif',
          textAlign,
        }}
        title={title}
      >
        {title || "-"}
      </div>
      {subtitle && (
        <>
          <div style={dividerStyle} />
          <div
            style={{
              wordBreak: "break-word",
              lineHeight: "1.25",
              marginTop: "3px",
              fontSize: "20px",
              fontWeight: 500,
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
            title={subtitle}
          >
            {subtitle}
          </div>
        </>
      )}
      {meta.length > 0 && (
        <>
          {(title || subtitle) && (
            <div style={dividerStyle} />
          )}
          <ul
            className="list-unstyled mb-0 mt-1"
            style={{
              lineHeight: "1.3",
              fontSize: "18px",
              color: textColor,
              fontFamily: '"PP Neue Montreal", sans-serif',
              textAlign,
            }}
          >
            {meta.map((item, idx) => (
              <li
                key={idx}
                style={{
                  wordBreak: "break-word",
                  color: textColor,
                  fontFamily: '"PP Neue Montreal", sans-serif',
                  textAlign,
                }}
              >
                {item}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

// Export buildFlowElements function - this is the complete function from KeyDiagram.js
export const buildFlowElements = (reportData, onEditClick, options = {}) => {
  if (!reportData) {
    return {
      nodes: [],
      edges: [],
      diagramHeight: 900,
      totalPages: 1,
      page: 0,
      totalDestinations: 0,
    };
  }

  const nodes = [];
  const edges = [];
  const spacingY = 210;
  const allDestinations = reportData.destinations || [];
  const destinationsPerPage = Math.max(
    1,
    Number(options.destinationsPerPage || DEFAULT_DESTINATIONS_PER_PAGE)
  );
  const totalPages = Math.max(1, Math.ceil(allDestinations.length / destinationsPerPage));
  const page = Math.min(Math.max(Number(options.page || 0), 0), totalPages - 1);
  const pageStartIndex = page * destinationsPerPage;
  const destinations = allDestinations.slice(pageStartIndex, pageStartIndex + destinationsPerPage);
  
  // Determine if we should split into 5-column layout for better A4 usage.
  // Max 7 destinations per column — pairs with DEFAULT_DESTINATIONS_PER_PAGE=14
  // for a BALANCED 7+7 full-page layout. Portrait A4 accommodates 7 rows +
  // the top-left cost box comfortably (fit math verified: layout ~1738px tall
  // scales to ~566pt on ~654pt available band, ~110pt to spare below the
  // last row so the footer never overlaps).
  const MAX_DESTINATIONS_PER_COLUMN = 7;
  const shouldSplitColumns = destinations.length > MAX_DESTINATIONS_PER_COLUMN;
  const splitIndex = shouldSplitColumns ? MAX_DESTINATIONS_PER_COLUMN : destinations.length;
  
  // Debug logging
  if (shouldSplitColumns && page === 0) {
    console.log(`[diagramUtils] Splitting ${destinations.length} destinations into two columns:`, {
      splitIndex,
      leftColumnCount: splitIndex,
      rightColumnCount: destinations.length - splitIndex
    });
  }
  
  // topOffset (first destination row Y) and rootY are defined AFTER allCosts is
  // built (below): the cost-details box sits in the top-left corner (x=30, same left
  // edge as the incoming column, so it does NOT widen the diagram) and the tree is
  // pushed down by the box height so the box never overlaps the root/first row.

  // Collect all costs (direct costs + transfer costs) BEFORE positioning cost box.
  const allCosts = [];
  
  // Add direct costs (filter out costs with 0.00 value)
  if (reportData.costs && reportData.costs.length > 0) {
    reportData.costs.forEach((cost) => {
      const amount = parseCHNumber(cost.value_without_vat || cost.cost_value || cost.value_with_vat);
      if (amount === 0 || amount === null || amount === undefined || isNaN(amount)) {
        return;
      }
      
      allCosts.push({
        code: cost.cost_number || cost.cost_id || "",
        description: cost.cost_name || "Cost",
        amount: amount,
        type: "direct",
      });
    });
  }
  
  // Collect transfer costs from incoming transfers.
  // Aggregate by (source key, cost number, cost name): sum amounts across *destinations* only.
  // API can return duplicate rows per (source, cost, destination) e.g. with_vat and without_vat;
  // count each (source, cost, destination) once (prefer without_vat), then sum by (source, cost).
  const transferCostsList = [];
  const transferCostsByKey = new Map(); // key: `${sourceKeyName}|${costNumber}|${costName}` -> { ..., byDestination: Map<destKey, { amount, result_without_vat, result_with_vat }> }
  if (reportData.incoming_transfers && reportData.incoming_transfers.length > 0) {
    reportData.incoming_transfers.forEach((transfer, transferIdx) => {
      const calculationResults = Array.isArray(transfer.calculation_results) 
        ? transfer.calculation_results 
        : [];
      
      calculationResults.forEach((calcResult, calcIdx) => {
        const sourceKeyName = transfer.source_key_name || "Unknown";
        
        const amountRaw = calcResult.result_without_vat !== null && calcResult.result_without_vat !== undefined
          ? calcResult.result_without_vat
          : (calcResult.result_with_vat !== null && calcResult.result_with_vat !== undefined
            ? calcResult.result_with_vat
            : (calcResult.result_value !== null && calcResult.result_value !== undefined
              ? calcResult.result_value
              : null));
        const amount = amountRaw !== null && amountRaw !== undefined ? parseFloat(amountRaw) : 0;
        
        const costName = calcResult.cost_name || null;
        const costNumber = calcResult.cost_number != null ? String(calcResult.cost_number) : null;
        
        const destinationNumber =
          calcResult.consumption_number ||
          calcResult.surface_number ||
          "";
        const destinationType = calcResult.destination_type || "";
        const destinationName = 
          calcResult.consumption_name ||
          calcResult.surface_name ||
          calcResult.label ||
          "";
        
        if (amount === 0 || amount === null || amount === undefined || isNaN(amount)) {
          return;
        }
        
        const hasCostNumber = costNumber && costNumber !== "null" && costNumber !== "undefined" && costNumber.trim() !== "";
        const hasCostName = costName && costName !== "null" && costName !== "undefined" && costName.trim() !== "";
        
        let displayName = "";
        if (hasCostNumber && hasCostName) {
          displayName = `${sourceKeyName} / ${costNumber} - ${costName}`;
        } else if (hasCostName) {
          displayName = `${sourceKeyName} / ${costName}`;
        } else if (hasCostNumber) {
          displayName = `${sourceKeyName} / ${costNumber}`;
        } else {
          displayName = `${sourceKeyName} / Transfer`;
        }
        
        const destinationLabel = destinationNumber && destinationName
          ? `${destinationNumber} - ${destinationName}`
          : destinationNumber || destinationName || "";
        
        const calcId = calcResult.id || calcResult.calculation_id || '';
        const uniqueId = calcId 
          ? `transfer-${transferIdx}-${calcId}`
          : `transfer-${transferIdx}-calc-${calcIdx}`;
        
        const dedupeKey = `${sourceKeyName}|${costNumber ?? ''}|${costName ?? ''}`;
        const destKey = destinationNumber || `dest-${transferIdx}-${calcIdx}`;
        const addWithoutVat = calcResult.result_without_vat != null && !isNaN(parseFloat(calcResult.result_without_vat))
          ? parseFloat(calcResult.result_without_vat) : 0;
        const addWithVat = calcResult.result_with_vat != null && !isNaN(parseFloat(calcResult.result_with_vat))
          ? parseFloat(calcResult.result_with_vat) : 0;
        const hasWithoutVat = addWithoutVat > 0;

        let entry = transferCostsByKey.get(dedupeKey);
        if (entry) {
          const byDest = entry.byDestination || new Map();
          const existingDest = byDest.get(destKey);
          if (existingDest) {
            const existingHasWithoutVat = (Number(existingDest.result_without_vat) || 0) > 0;
            if (hasWithoutVat && !existingHasWithoutVat) {
              byDest.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
            }
            return;
          }
          byDest.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
          entry.byDestination = byDest;
          return;
        }
        const byDestination = new Map();
        byDestination.set(destKey, { amount, result_without_vat: addWithoutVat, result_with_vat: addWithVat });
        transferCostsByKey.set(dedupeKey, {
          id: uniqueId,
          code: costNumber || sourceKeyName,
          costName: costName,
          costNumber: costNumber,
          description: displayName,
          sourceKey: sourceKeyName,
          destinationNumber: destinationNumber,
          destinationName: destinationName,
          destinationLabel: destinationLabel,
          destinationType: destinationType,
          amount: amount,
          result_without_vat: addWithoutVat || calcResult.result_without_vat,
          result_with_vat: addWithVat || calcResult.result_with_vat,
          result_value: calcResult.result_value,
          type: "transfer",
          byDestination,
        });
      });
    });
    transferCostsByKey.forEach((entry) => {
      if (entry.byDestination && entry.byDestination.size > 0) {
        let sumAmount = 0;
        let sumWithoutVat = 0;
        let sumWithVat = 0;
        entry.byDestination.forEach((d) => {
          sumAmount += Number(d.amount) || 0;
          sumWithoutVat += Number(d.result_without_vat) || 0;
          sumWithVat += Number(d.result_with_vat) || 0;
        });
        entry.amount = sumAmount;
        entry.result_without_vat = sumWithoutVat;
        entry.result_with_vat = sumWithVat;
      }
      delete entry.byDestination;
    });
    transferCostsList.push(...transferCostsByKey.values());
  }
  
  transferCostsList.forEach((transferCost) => {
    allCosts.push(transferCost);
  });

  // Root and first destination row are ALWAYS pinned to the top of the diagram —
  // client explicitly asked for the key-name box to be "always in the same place
  // on each key". Cost box floats top-RIGHT below (see costDetailsNode section);
  // it may grow with a tall cost list but never displaces the root/tree.
  const rootY = 0;
  const topOffset = 100;

  // Add root node first
  const rootId = `key-${reportData.key.id}`;

  // Total consumption of the key — displayed on the root node (client request).
  // Only shown for keys that actually carry a consumption total.
  const rootMeta = [];
  const keyTotalConsumption = Number(reportData.key?.total_consumption);
  if (Number.isFinite(keyTotalConsumption) && keyTotalConsumption > 0) {
    const totalLabel = formatNumberWithUnit(
      keyTotalConsumption,
      reportData.key?.total_consumption_unit || "kWh"
    );
    if (totalLabel) rootMeta.push(totalLabel);
  }

  nodes.push({
    id: rootId,
    position: { x: columnX.root, y: rootY },
    data: {
      label: (
        <NodeContent
          title={reportData.key.key_name}
          meta={rootMeta}
          textColor="#00033F"
          textAlign="center"
          centerContent
        />
      ),
    },
    style: nodeStyles.root,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  });
  
  // Add cost details node - single box with all costs.
  // TOP-RIGHT placement (client request 2026-07-17 — reverted from the top-left
  // experiment in f2ad0b8 which the client rejected). The box sits above the
  // rightmost destination column so it never overlaps the tree horizontally, and
  // its BOTTOM edge is aligned with the root node's bottom (so short cost lists
  // sit at root height and tall cost lists grow upward). This matches the client's
  // reference PDF (Étang-B_Répartition-24_20260402-3.pdf pages 14-17/28), where
  // Eau chaude / Chaud / etc. trees show the cost details box in the top-right
  // corner and the key name grey box in the top-left corner — always in the same
  // position on every key.
  //
  // costBoxX picks the last-column-in-use so the box sits directly above the
  // right-most tree column. costBoxY is anchored to the ROOT node's bottom so
  // short lists sit next to the root and long lists extend upward (Y may go
  // negative for very tall cost lists; the diagram bbox is corrected by the
  // measured DOM in diagramBounds.js so no clipping).
  if (allCosts.length > 0) {
    const rootNodeMinH = 80; // must match nodeBaseStyle.minHeight — see NODE_WIDTH block above
    const COST_ROW_H = 56;   // per-cost row height incl. a wrapped 2nd line
    const COST_BOX_PAD = 24; // 12px top + 12px bottom padding
    const costBoxHeightEstimate = COST_BOX_PAD + allCosts.length * COST_ROW_H;
    const costBoxY = (rootY + rootNodeMinH) - costBoxHeightEstimate;
    const costBoxX = shouldSplitColumns ? columnX.buyer2 - 150 : columnX.buyer1;

    const costDetailsNode = {
      id: "cost-details",
      position: { x: costBoxX, y: costBoxY },
      data: {
        label: (
          <div
            style={{
              padding: "12px",
              fontFamily: '"PP Neue Montreal", sans-serif',
              fontSize: "20px",
              color: "#00033F",
              background: "#ffffff",
              border: "1px solid #d8dee9",
              borderRadius: "8px",
              // Fixed width sized for the longest "code - description   amount" row
              // (fontSize 20px, amounts like "103'607.20 CHF"). Was minWidth:280,
              // which let the box stay narrow while the nowrap amount painted past
              // it and got clipped in the PDF capture. A real width makes the box's
              // measured width equal its painted width so diagramBounds captures it.
              width: "420px",
              boxSizing: "border-box",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {[...allCosts].sort((a, b) => {
                const codeA = String(a.code ?? "");
                const codeB = String(b.code ?? "");
                const numA = Number.parseFloat(codeA);
                const numB = Number.parseFloat(codeB);
                if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
                return codeA.localeCompare(codeB);
              }).map((cost, idx) => {
              const formattedAmount = formatNumberCH(cost.amount, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              
              let costLabel = "";
              if (cost.type === "transfer") {
                costLabel = cost.description || `${cost.sourceKey || "Unknown"} / Transfer`;
              } else {
                if (cost.code && cost.description) {
                  costLabel = `${cost.code} - ${cost.description}`;
                } else if (cost.code) {
                  costLabel = cost.code;
                } else if (cost.description) {
                  costLabel = cost.description;
                } else {
                  costLabel = "Cost";
                }
              }
              
              return (
                <div
                  key={cost.id || `${cost.type}-${cost.code}-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: idx < allCosts.length - 1 ? "8px" : "0",
                    paddingBottom: idx < allCosts.length - 1 ? "8px" : "0",
                    borderBottom: idx < allCosts.length - 1 ? "1px solid #e0e0e0" : "none",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      flex: "1 1 auto",
                      textAlign: "left",
                      marginRight: "12px",
                      // No ellipsis: let the label take its natural width so the
                      // fixed-width box holds the full "code - description" text
                      // and the amount is never clipped.
                      wordBreak: "break-word",
                    }}
                  >
                    <span style={{ color: "#00033F", fontWeight: 500 }}>
                      {costLabel}
                    </span>
                  </div>
                  <div 
                    style={{ 
                      flex: "0 0 auto",
                      textAlign: "right",
                      fontWeight: 600, 
                      color: "#00033F", 
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formattedAmount} CHF
                  </div>
                </div>
              );
            })}
          </div>
        ),
      },
      style: {
        background: "transparent",
        border: "none",
        padding: 0,
        width: "auto",
        height: "auto",
      },
      draggable: false,
      selectable: false,
    };
    nodes.push(costDetailsNode);
  }

  const destinationPositions = new Map();
  const destinationUniqueKeys = [];
  const destinationColumnMap = new Map(); // Track which column each destination is in
  
  destinations.forEach((destination, index) => {
    const uniqueKey = destination.id != null 
      ? `dest-${destination.id}`
      : `dest-idx-${index}`;
    
    destinationUniqueKeys[index] = uniqueKey;
    
    // Calculate Y position: if split, first half in left column, second half in right column
    // Add topOffset to start below the root node and cost box
    let y, column;
    if (shouldSplitColumns) {
      if (index < splitIndex) {
        // First half: left column
        y = topOffset + index * spacingY;
        column = 'left';
      } else {
        // Second half: right column (aligned with first half)
        y = topOffset + (index - splitIndex) * spacingY;
        column = 'right';
      }
    } else {
      // Single column
      y = topOffset + index * spacingY;
      column = 'left';
    }
    
    const destNodeId = uniqueKey;
    destinationPositions.set(uniqueKey, y);
    destinationColumnMap.set(uniqueKey, column);

    const surfaceNumber =
      destination.destination_number ||
      destination.destination_id ||
      "Surface";
    
    let surfaceName = destination.destination_name;
    
    if (!surfaceName && destination.destination_label) {
      const label = destination.destination_label;
      
      let match = label.match(/\d+\s*[-–]\s*(.+?)(?:\s*\(|$)/i);
      if (match && match[1]) {
        const extracted = match[1].trim();
        if (extracted && extracted !== 'Surface' && !/^\d+$/.test(extracted)) {
          surfaceName = extracted;
        }
      }
      
      if (!surfaceName) {
        match = label.match(/(.+?)\s*\(\s*\d+/);
        if (match && match[1]) {
          const extracted = match[1].trim();
          if (extracted && extracted !== 'Surface' && extracted !== surfaceNumber) {
            surfaceName = extracted;
          }
        }
      }
      
      if (!surfaceName) {
        const cleaned = label
          .replace(new RegExp('^\\s*' + surfaceNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[-–]?\\s*', 'gi'), '')
          .replace(/\s*\([^)]*\)\s*$/, '')
          .replace(/\s*\d+[\s,]?\d*\s*(m²|kWh|CHF)\s*/gi, '')
          .trim();
        
        if (cleaned && 
            cleaned !== 'Surface' && 
            cleaned !== surfaceNumber && 
            cleaned !== '-' &&
            !/^\d+$/.test(cleaned)) {
          surfaceName = cleaned;
        }
      }
    }
    
    if ((!surfaceName || surfaceName === surfaceNumber) && reportData.incoming_transfers) {
      for (const transfer of reportData.incoming_transfers) {
        if (transfer.calculation_results && Array.isArray(transfer.calculation_results)) {
          for (const calcResult of transfer.calculation_results) {
            const calcSurfaceNumber = calcResult.surface_number || calcResult.consumption_number || calcResult.variables?.surface_number;
            const calcSurfaceName = calcResult.surface_name || calcResult.consumption_name || calcResult.variables?.surface_label;
            const calcSurfaceId = calcResult.variables?.surface_id || calcResult.surface_id;
            
            const matchesByNumber = calcSurfaceNumber && String(calcSurfaceNumber) === String(surfaceNumber);
            const matchesById = calcSurfaceId && String(calcSurfaceId) === String(destination.destination_id);
            const matchesByDestinationId = String(destination.destination_id) === String(surfaceNumber);
            
            if ((matchesByNumber || matchesById || matchesByDestinationId) && calcSurfaceName && calcSurfaceName.trim()) {
              surfaceName = calcSurfaceName.trim();
              break;
            }
            
            if ((matchesByNumber || matchesById || matchesByDestinationId) && !surfaceName && calcResult.label) {
              const labelMatch = calcResult.label.match(/\d+\s*[-–]\s*(.+?)(?:\s*\(|$)/i);
              if (labelMatch && labelMatch[1] && labelMatch[1].trim() !== 'Surface' && !/^\d+$/.test(labelMatch[1])) {
                surfaceName = labelMatch[1].trim();
                break;
              }
            }
          }
        }
        if (surfaceName && surfaceName !== surfaceNumber) break;
      }
    }
    
    const destinationType = destination.destination_type?.toLowerCase() || "";
    const isConsumption = destinationType === "consumption";
    
    // For consumption, also check destination_name early if not already set
    // This ensures we use the backend-provided name if available
    if (isConsumption && (!surfaceName || surfaceName === surfaceNumber)) {
      if (destination.destination_name && destination.destination_name.trim()) {
        surfaceName = destination.destination_name.trim();
        console.log(`[diagramUtils] Using destination_name for consumption ${destination.destination_id}: ${surfaceName}`);
      }
    }
    
    // Final fallback - but for consumption, NEVER use destination_key_name
    // destination_key_name is only for target keys (green nodes on the right)
    if (!surfaceName || surfaceName === surfaceNumber) {
      if (isConsumption) {
        // For consumption: try destination_name (from backend) one more time
        // The enrichment from incoming transfers should have already set surfaceName if available
        // But if not, try destination_name directly
        if (destination.destination_name && destination.destination_name.trim()) {
          surfaceName = destination.destination_name.trim();
        } else {
          // Debug: Log what we have for consumption destinations
          console.log(`[diagramUtils] Consumption destination ${destination.destination_id} missing name:`, {
            destination_id: destination.destination_id,
            destination_name: destination.destination_name,
            destination_label: destination.destination_label,
            destination_number: destination.destination_number,
            destination_key_name: destination.destination_key_name,
            surfaceName: surfaceName,
            surfaceNumber: surfaceNumber
          });
          // If still not found, show null (component will handle it)
          // Do NOT use destination_key_name for consumption
          surfaceName = null;
        }
      } else {
        // For surface/other types: can use destination_key_name as fallback
        if (destination.destination_key_name && destination.destination_key_name.trim()) {
          surfaceName = destination.destination_key_name.trim();
        } else if (destination.destination_type && 
            destination.destination_type.toLowerCase() !== 'surface' &&
            destination.destination_type !== '-') {
          surfaceName = destination.destination_type;
        } else {
          surfaceName = null;
        }
      }
    }

    const destinationValueLabel =
      destination.destination_value !== null && destination.destination_value !== undefined
        ? formatNumberWithUnit(
            destination.destination_value,
            destination.destination_unit ||
              (destination.destination_type === "surface"
                ? "m²"
                : destination.destination_type === "consumption"
                ? "kWh"
                : "")
          )
        : "-";
    
    const nodeTitle = surfaceNumber;

    const nodeStyle = isConsumption
      ? nodeStyles.consumption 
      : destinationType === "surface"
      ? nodeStyles.surface
      : nodeStyles.destination;

    // Determine X position based on column
    const destX = column === 'right' ? columnX.destination2 : columnX.destination;
    
    // For consumption nodes: same display order as surface nodes
    // 1. Number (title) - top
    // 2. Name (subtitle) - middle  
    // 3. Value (meta) - bottom
    // Always ensure subtitle is a string (not null) for consumption nodes so it renders
    const consumptionSubtitle = truncateText(
      (surfaceName && surfaceName.trim() && surfaceName !== "-")
        ? surfaceName.trim()
        : (destination.destination_name && destination.destination_name.trim()
            ? destination.destination_name.trim()
            : "-")
    );
    
    const nodeContentProps = isConsumption
      ? {
          title: nodeTitle,  // Consumption number (e.g., "306")
          subtitle: consumptionSubtitle,  // Consumption name (e.g., "2 appartements en attique")
          meta: destinationValueLabel !== "-" ? [destinationValueLabel] : [],  // Value (e.g., "19132 kWh")
          textColor: "#ffffff"
        }
      : {
          title: nodeTitle,
          subtitle: truncateText(surfaceName),
          meta: destinationValueLabel !== "-" ? [destinationValueLabel] : [],
          textColor: "#ffffff"
        };
    
    const nodeToAdd = {
      id: destNodeId,
      type: 'default',
      position: { 
        x: destX, 
        y: y
      },
      data: {
        label: (
          <NodeContent
            {...nodeContentProps}
          />
        ),
      },
      style: {
        ...nodeStyle,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
    
    nodes.push(nodeToAdd);

    // Only create edge from root to destination if no_cost_destination is false
    if (!destination.no_cost_destination) {
      edges.push({
        id: `edge-${rootId}-${destNodeId}`,
        source: rootId,
        target: destNodeId,
        type: "labeled",
        label: `${destination.ponderation || 1}`,
        labelStyle: {
          fontSize: 20,
          fontWeight: 600,
        },
        data: {
          column: column, // Pass column info ('left' or 'right') to edge
        },
      });
    }
  });

  destinations.forEach((destination, index) => {
    const uniqueKey = destinationUniqueKeys[index];
    if (!uniqueKey) {
      return;
    }
    
    const destNodeId = uniqueKey;
    const destY = destinationPositions.get(uniqueKey) || 0;
    const destColumn = destinationColumnMap.get(uniqueKey) || 'left';
    
    // Always create buyer/target nodes and edges, regardless of no_cost_destination flag
    // The no_cost_destination flag only affects the root-to-destination edge
    // Determine buyer/target column based on destination column
    // If splitting (10+ destinations): Column 2 → Column 3, Column 4 → Column 5
    // If not splitting (≤10 destinations): All destinations → Single buyer/target column
    const buyerTargetX = shouldSplitColumns 
      ? (destColumn === 'right' ? columnX.buyer2 : columnX.buyer1)
      : columnX.buyer1; // Use single column for ≤10 destinations

    if (destination.buyer || destination.selected_object) {
      // Right-hand box. For object-routed destinations (Object flow + Consumption→
      // Object) it shows the OBJECT (object code on top, parent surface below) in a
      // light-grey box; otherwise the normal Buyer.
      const selectedObject = destination.selected_object;
      const buyerIdPart = destination.buyer?.id ?? `obj-${selectedObject?.id ?? "x"}`;
      const buyerKey = `buyer-${buyerIdPart}-${uniqueKey}`;

      const objCode = selectedObject
        ? ((selectedObject.objet_code && String(selectedObject.objet_code).trim()) || String(selectedObject.object || ""))
        : "";
      // Buyer (grey) box content — client feedback 2026-07-17 & call: show number
      // AND name on a SINGLE title line ("only one line for the number"), no
      // subtitle divider. Matches the client's reference PDF where buyer boxes
      // read like "90001 Vente N0.C1". Object-routed boxes (selectedObject) keep
      // objet_code as the title and tenant as subtitle, unchanged.
      const buyerNumber = selectedObject
        ? objCode
        : `${destination.buyer.buyer_number || ""}`.trim();
      const buyerName = selectedObject
        ? `${selectedObject.tenant_name || ""}`.trim()
        : (destination.buyer.name || "Buyer");
      const buyerTitle = selectedObject
        ? buyerNumber
        : [buyerNumber, buyerName].filter(Boolean).join(" ");
      const buyerSubtitle = selectedObject ? truncateText(buyerName) : "";
      const buyerMeta = [];

      nodes.push({
        id: buyerKey,
        position: { x: buyerTargetX, y: destY },
        data: {
          label: (
            <NodeContent
              title={buyerTitle}
              subtitle={buyerSubtitle}
              meta={buyerMeta}
              textColor="#00033F"
            />
          ),
        },
        style: selectedObject ? nodeStyles.object : nodeStyles.buyer,
        targetPosition: Position.Left,
      });

      edges.push({
        id: `edge-${destNodeId}-${buyerKey}`,
        source: destNodeId,
        target: buyerKey,
        type: "straight",
      });
    }

    if (destination.target_key) {
      const targetId = destination.target_key.id || destination.target_key.key_name;
      const nodeId = `target-${targetId}-${uniqueKey}`;
      
      nodes.push({
        id: nodeId,
        position: { x: buyerTargetX, y: destY },
        data: {
          label: (
            <NodeContent
              title={destination.target_key.key_name}
              textColor="#00033F"
              textAlign="center"
              centerContent
            />
          ),
        },
        style: nodeStyles.target,
        targetPosition: Position.Left,
      });

      edges.push({
        id: `edge-${destNodeId}-${nodeId}`,
        source: destNodeId,
        target: nodeId,
        type: "straight",
      });
    }
  });

  // FIXED page geometry — same bbox for EVERY key, regardless of how many
  // destinations the key has (client 2026-07-22: "Can you fix a standard size for
  // every key even when we have only one box?"). Small keys used to shrink their
  // spacer to (buyer1 + node + pad ≈ 1120) which produced a narrower captured
  // frame than large split-column keys (spacer at ≈ 1680). When both were then
  // fit to the A4 page, the small key's content scaled up more, and elements at
  // the actual right edge (cost box, when the buyer2 column isn't in use) sat
  // outside the tracked bbox and got clipped in the exported PNG. Pinning the
  // spacer to the MAXIMUM possible layout extent (as if every key ran the full
  // 5-column split layout with 7 rows of destinations) gives every diagram the
  // same aspect ratio and the same page-fill on export — matching the Incendie
  // frame the client called out as the reference size.
  const NODE_W = NODE_WIDTH;                                     // must match nodeBaseStyle.width
  const FIXED_PAGE_ROWS = 7;                                     // matches full page
  // Bottom pad boosted 100→180 (client 2026-07-22 SS4: on the Eau chaude key the
  // 3053 "ECS résidence étudiante" box on the last row had its kWh value line
  // clipped in the capture — consumption boxes carry a 3rd line for value+unit
  // and grow taller than the 80 px surface-box minHeight, so the previous 100 px
  // pad wasn't enough breathing room below the last row).
  const BOTTOM_PAD = 180;
  const fullPageHeight = topOffset + (FIXED_PAGE_ROWS - 1) * spacingY + BOTTOM_PAD;
  // Right-edge budget covers BOTH the widest buyer column (buyer2 + NODE_W = 1640)
  // AND the cost-details box (spans up to buyer2 - 150 + 420 = 1700). Adding a 40 px
  // safety pad → 1740. Client feedback SS4 (Eau chaude): the "3100 - Eau chaude
  // sanitaire" value was clipped because cost box (1700) sat past the previous
  // 1680 spacer.
  const COST_BOX_WIDTH = 420;
  const COST_BOX_RIGHT_EDGE = (columnX.buyer2 - 150) + COST_BOX_WIDTH; // 1700
  const BUYER_RIGHT_EDGE = columnX.buyer2 + NODE_W;                    // 1640
  const fullPageRightX = Math.max(COST_BOX_RIGHT_EDGE, BUYER_RIGHT_EDGE) + 40; // 1740
  const diagramHeight = Math.max(400, fullPageHeight);
  nodes.push({
    id: "__spacer-bottom-right__",
    position: { x: fullPageRightX, y: fullPageHeight },
    data: { label: "" },
    style: { width: 1, height: 1, opacity: 0, border: "none", padding: 0, minHeight: 0 },
    selectable: false,
    draggable: false,
  });

  return {
    nodes,
    edges,
    diagramHeight,
    totalPages,
    page,
    totalDestinations: allDestinations.length,
  };
};

